import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogicImage } from "./LogicImage";
import { useSEO } from "@/lib/useSEO";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import {
  fetchChamber, fetchChamberSession, fetchCourse, fetchProgress,
  saveChamberStage, submitChamber,
} from "@/lib/logicApi";
import { useLogicUi } from "./logicUi";
import { BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";

const LEVEL_TONES = {
  Demonstrated: "border-gold/60 text-gold",
  Practised: "border-[#8fa3c8]/60 text-[#b9c6de]",
  Developing: "border-amber-400/50 text-amber-300",
  "Requires Further Practice": "border-red-400/40 text-red-300",
};

function Stage({ stage, initial, onContinue, isLast }) {
  const { ui, L, opt } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [resp, setResp] = useState(initial || {});
  const [err, setErr] = useState("");
  const set = (k, v) => setResp((x) => ({ ...x, [k]: v }));

  const validate = () => {
    if (stage.kind === "classification" || stage.kind === "assent") {
      const missing = (stage.items || []).some((_, i) => !resp[String(i)]);
      if (missing) return ui.vAnswerEvery;
      if (stage.why_field && !(resp.why || "").trim()) return ui.vWhy;
    } else {
      const missing = (stage.fields || []).some((f) => !(resp[f.id] || "").trim());
      if (missing) return ui.vComplete;
      if (stage.kind === "judgment" && !resp.certainty) return ui.vCertainty;
      if (stage.kind === "judgment" && !(resp.certaintyWhy || "").trim()) return ui.vCertaintyWhy;
    }
    return "";
  };

  return (
    <section className="space-y-6" data-testid={`chamber-stage-${stage.id}`}>
      <div>
        <h2 className="font-serif text-2xl text-bone">{L(stage.title)}</h2>
        <p className="mt-3 text-fog font-reading leading-[1.9]">{L(stage.instruction)}</p>
      </div>

      {(stage.kind === "classification" || stage.kind === "assent") && (
        <div className="space-y-4">
          {stage.items.map((it, i) => (
            <div key={i} className="border border-[#c8a95a]/15 bg-[#171c26] p-4">
              <p className="text-bone font-reading text-sm mb-3">{L(it.text)}</p>
              <div className="flex flex-wrap gap-2">
                {stage.options.map((o) => (
                  <button key={o} data-testid={`chamber-${stage.id}-${i}-${o}`}
                    onClick={() => set(String(i), o)} aria-pressed={resp[String(i)] === o}
                    className={`px-3 py-2 text-[11px] tracking-[0.14em] uppercase border transition-colors ${resp[String(i)] === o ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-fog/70 hover:border-gold/40"}`}>
                    {opt(o)}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {stage.why_field && (
            <div>
              <label className="block text-sm text-bone/90 font-reading mb-2">{ui.chamberWhy}</label>
              <textarea rows={3} value={resp.why || ""} onChange={(e) => set("why", e.target.value)} data-testid={`chamber-${stage.id}-why`}
                className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          )}
        </div>
      )}

      {(stage.kind === "freeform" || stage.kind === "judgment") && (
        <div className="space-y-6">
          {stage.fields.map((f) => (
            <div key={f.id}>
              <label className="block text-sm text-bone/90 font-reading mb-2">{L(f.label)}</label>
              <textarea rows={4} value={resp[f.id] || ""} onChange={(e) => set(f.id, e.target.value)}
                data-testid={`chamber-field-${f.id}`}
                className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          ))}
          {stage.certainty && (
            <div>
              <p className="text-sm text-bone/90 font-reading mb-2">{ui.certainty}</p>
              <div className="flex gap-2 mb-3">
                {["low", "moderate", "high"].map((c) => (
                  <button key={c} data-testid={`chamber-certainty-${c}`} onClick={() => set("certainty", c)} aria-pressed={resp.certainty === c}
                    className={`px-4 py-2 text-[11px] tracking-[0.16em] uppercase border ${resp.certainty === c ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-fog/70 hover:border-gold/40"}`}>{opt(c)}</button>
                ))}
              </div>
              <textarea rows={2} value={resp.certaintyWhy || ""} onChange={(e) => set("certaintyWhy", e.target.value)}
                data-testid="chamber-certainty-why" placeholder={ui.certaintyPh}
                className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          )}
        </div>
      )}

      {err && <p className="text-sm text-red-300" data-testid="chamber-stage-error">{err}</p>}
      <button data-testid="chamber-stage-continue"
        onClick={() => { const e = validate(); if (e) { setErr(e); } else { setErr(""); onContinue(resp); } }}
        className={`px-8 py-4 text-[11px] font-semibold tracking-[0.22em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
        {isLast ? ui.presentExam : ui.continueBtn}
      </button>
    </section>
  );
}

function Evaluation({ evaluation, chamber, media }) {
  const { ui, L } = useLogicUi();
  return (
    <div className="space-y-12" data-testid="chamber-evaluation">
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase text-[#8fa3c8] mb-3">{ui.examinersReading}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {evaluation.rubric.map((r) => (
            <div key={r.id} className="border border-[#c8a95a]/15 bg-[#171c26] p-5" data-testid={`chamber-rubric-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="font-serif text-bone">{r.label}</p>
                <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-1 border whitespace-nowrap ${LEVEL_TONES[r.level] || "border-white/20 text-fog"}`}>{ui.levels[r.level] || r.level}</span>
              </div>
              <p className="mt-3 text-[13px] text-fog/85 font-reading leading-relaxed">{r.reading}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="border-l-2 border-gold/50 pl-6 py-2">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-3">{ui.oneExaminer}</p>
        <p className="font-reading text-fog leading-[1.95] whitespace-pre-line" data-testid="chamber-closing">{evaluation.closing}</p>
      </div>
      <div className="border border-gold/40 bg-[#f3e9d2]/[0.05] p-8 text-center space-y-4" data-testid="chamber-recognition">
        <p className="text-[10px] tracking-[0.35em] uppercase text-[#8fa3c8]">{ui.recognition}</p>
        <p className="font-serif text-3xl text-gold">{L(chamber.recognitionTitle)}</p>
        <p className="text-sm text-fog/85 font-reading leading-[1.9] max-w-xl mx-auto">{L(chamber.recognitionText)}</p>
      </div>
      <div className="max-w-xl mx-auto"><LogicImage media={media} assetKey="logic-12-return-to-life" alt="" /></div>
      <p className="text-center text-fog/70 font-reading italic max-w-xl mx-auto">{L(chamber.closing)}</p>
      <div className="text-center">
        <Link to="/academy/logic" className="inline-block text-[11px] tracking-[0.22em] uppercase text-gold border border-gold/50 px-6 py-3 hover:bg-gold/10">{ui.returnHall}</Link>
      </div>
    </div>
  );
}

export default function ChamberOfJudgment() {
  const { ui, L } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [chamber, setChamber] = useState(null);
  const [course, setCourse] = useState(null);
  const [records, setRecords] = useState([]);
  const [session, setSession] = useState(null);
  const [stageIdx, setStageIdx] = useState(-1); // -1 = antechamber
  const [submitting, setSubmitting] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [err, setErr] = useState("");

  useSEO({ title: "The Chamber of Judgment | Stoic Logic | StoicOs.ai Academy", canonical: "https://stoicos.ai/academy/logic/chamber-of-judgment" });

  useEffect(() => {
    fetchChamber().then(setChamber).catch(() => {});
    fetchCourse().then(setCourse).catch(() => {});
    fetchProgress().then((d) => setRecords(d.records || []));
    fetchChamberSession().then((d) => {
      setSession(d.session);
      if (d.session?.evaluation) setEvaluation(d.session.evaluation);
    });
  }, []);

  if (!chamber || !course) return <div className="min-h-screen bg-ink" />;
  const media = course.media || {};
  const total = course.phases.reduce((n, p) => n + p.lessons.length, 0);
  const walked = records.length;
  const open = walked >= total;
  const stages = chamber.stages;

  const onContinue = async (resp) => {
    const stage = stages[stageIdx];
    await saveChamberStage(stage.id, resp);
    if (stageIdx + 1 < stages.length) {
      setStageIdx(stageIdx + 1);
      window.scrollTo({ top: 0 });
    } else {
      setSubmitting(true); setErr("");
      try {
        const d = await submitChamber();
        setEvaluation(d.evaluation);
      } catch (e) { setErr(e.message || "Submission failed"); }
      finally { setSubmitting(false); }
    }
  };

  return (
    <main className="min-h-screen bg-ink" data-testid="logic-chamber-page">
      <section className="relative min-h-[50vh] flex items-end">
        <div className="absolute inset-0"><LogicImage media={media} assetKey="logic-11-chamber-of-judgment" ratio="" className="absolute inset-0" alt="The Chamber of Judgment" /><div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" /></div>
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 pb-14 pt-40 w-full">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#8fa3c8] mb-3">{ui.finalAssessment}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-bone">{ui.chamberName}</h1>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        {evaluation ? (
          <Evaluation evaluation={evaluation} chamber={chamber} media={media} />
        ) : submitting ? (
          <div className="text-center py-24 space-y-6" data-testid="chamber-submitting">
            <p className="font-serif text-2xl text-bone">{ui.reading}</p>
            <p className="text-fog/70 font-reading">{ui.readingSub}</p>
            <div className="mx-auto w-10 h-10 border-2 border-gold/50 border-t-gold rounded-full animate-spin" aria-hidden />
          </div>
        ) : !open ? (
          <div className="space-y-8">
            <p className="font-reading text-fog leading-[1.95]">{ui.chamberLockedIntro}</p>
            <div className="border border-[#c8a95a]/25 bg-[#171c26] p-6" data-testid="chamber-locked">
              <p className="text-sm text-bone/90">{ui.lessonsWalked(walked, total)}</p>
              <p className="text-sm text-fog/70 mt-2">{ui.chamberLockedNote}</p>
            </div>
            <Link to="/academy/logic" className="inline-block text-[11px] tracking-[0.22em] uppercase text-gold border border-gold/50 px-6 py-3 hover:bg-gold/10">{ui.returnHall}</Link>
          </div>
        ) : stageIdx === -1 ? (
          <div className="space-y-10" data-testid="chamber-antechamber">
            <h2 className="font-serif text-2xl text-bone">{L(chamber.title)}</h2>
            <p className="font-reading text-fog leading-[1.95]">{L(chamber.intro)}</p>
            <div className="space-y-4">
              {chamber.testimonies.map((t, i) => (
                <div key={i} className="border border-[#8fa3c8]/25 bg-[#1a2130] p-6" data-testid={`chamber-testimony-${i}`}>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8] mb-2">{L(t.source)}</p>
                  <p className="font-reading italic text-fog/95 leading-[1.85]">{L(t.text)}</p>
                </div>
              ))}
            </div>
            <div className="border-l-2 border-gold/50 pl-6 py-1">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-2">{ui.pressure}</p>
              <p className="font-reading text-fog/90 leading-[1.9]">{L(chamber.pressure)}</p>
            </div>
            {session && !session.completedAt && Object.keys(session.stages || {}).length > 0 && (
              <p className="text-sm text-[#b9c6de]" data-testid="chamber-resume-note">{ui.resumeNote}</p>
            )}
            <button data-testid="chamber-begin" onClick={() => {
              const done = Object.keys(session?.completedAt ? {} : session?.stages || {});
              const next = stages.findIndex((s) => !done.includes(s.id));
              setStageIdx(next === -1 ? stages.length - 1 : next);
            }}
              className={`px-10 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
              {session && !session.completedAt && Object.keys(session.stages || {}).length > 0 ? ui.resumeExam : ui.enterUnhurried}
            </button>
          </div>
        ) : (
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8] mb-8" data-testid="chamber-stage-counter">{ui.movement(stageIdx + 1, stages.length)}</p>
            <Stage key={stages[stageIdx].id} stage={stages[stageIdx]}
              initial={session?.stages?.[stages[stageIdx].id]}
              onContinue={onContinue} isLast={stageIdx === stages.length - 1} />
            {err && <p className="mt-4 text-sm text-red-300">{err}</p>}
          </div>
        )}
      </section>
    </main>
  );
}
